import React, { useState, useEffect } from 'react'
import { Link, useHistory } from 'react-router-dom'
import queryString from 'query-string'
import moment from 'moment'
import { Triangle } from 'react-feather'

import TableContainer from '../TableContainer'
import Loading from '../../../templates/UI/Loading/Loading'
import Dollars from '../../../templates/Dollars/Dollars'
import Affiliate from '../../../templates/Affiliate/Affiliate'
import Campaign from '../../../templates/Campaign/Campaign'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Advertiser from '../../../templates/Advertiser/Advertiser'

const ReportCampaignTrends = ({ type, showSystemMessage }) => {
    const [dataLoaded, setDataLoaded] = useState(false)
    const [report, setReport] = useState([])
    const [totals, setTotals] = useState([])
    const [lastUpdated, setLastUpdated] = useState('')

    const spinner = <FontAwesomeIcon icon="spinner" spin />

    const history = useHistory()

    const urlParams = history.location.search ? queryString.parse(history.location.search) : ''

    let timeFrame = urlParams.t ? urlParams.t.trim() : 'd'
    let filterTrend = urlParams.filter_trend ? urlParams.filter_trend.trim() : 'b'
    let mgrId = urlParams.mgrid ? parseInt(urlParams.mgrid) : 0
    let bizMgrId = urlParams.bmid ? parseInt(urlParams.bmid) : 0
    let minT1Margin = urlParams.mint1 ? urlParams.mint1.trim() : ''
    let filterTrendDate = urlParams.date ? urlParams.date.trim() : ''

    useEffect(() => {
        getReport()
    }, [])

    const getReport = () => {
        const mbToken = localStorage.getItem('adm-auth-token')
        setDataLoaded(false)

        let url = ''

        if (type === 'affiliate') {
            url = `${process.env.REACT_APP_API_URL}/reports/affiliates/trend?t=${timeFrame}&filter_trend=${filterTrend}&type=a`
        } else if (type === 'advertiser') {
            url = `${process.env.REACT_APP_API_URL}/reports/advertisers/trend?t=${timeFrame}&filter_trend=${filterTrend}&type=m`
        } else {    
            url = `${process.env.REACT_APP_API_URL}/reports/offers/trend?t=${timeFrame}&filter_trend=${filterTrend}&type=o`
        }

        if (mgrId > 0) {
            url += `&mgrid=${mgrId}`
        }

        if (type === 'campaign' && bizMgrId > 0) {
            url += `&bmid=${bizMgrId}`
        }

        if (type === 'campaign' && filterTrendDate !== '') {
            url += `&date=${filterTrendDate}`
        }

        if (isNaN(minT1Margin)) {
            showSystemMessage('error', 'Minimum T1 margin must be a number')
        }

        if (minT1Margin.trim() !== '') {
            url += `&mint1=${minT1Margin}`
        }

        fetch(url, {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'x-app-token': mbToken,
            },
        })
            .then((res) => res.json())
            .then((result) => {
                if (result['success'] === true && result.report) {
                    setDataLoaded(true)
                    setLastUpdated(result.last_updated)
                    setReport(result.report)
                    setTotals(result.totals)
                } else if (result['success'] === false) {
                    if (result.code !== 401) {
                        showSystemMessage('error', 'There was an error fetching the data. Please try again.')
                    }
                    return
                } else {
                    showSystemMessage('error', result.errors.message)
                    return
                }
            })
            .catch((error) => {
                console.log('Error in ReportCampaignTrends: ', error)
                showSystemMessage('error', 'Connection Error.')
                return
            })
    }

    const getReportOutput = () => {
        return (
            <React.Fragment>
                <TableContainer
                    reportType={
                        type === 'affiliate'
                            ? 'affiliate trend'
                            : type === 'advertiser'
                                ? 'advertiser trend'
                                : 'campaign trend'
                    }

                    data={report}
                    totals={totals}
                    columns={[
                        {
                            Header: 'Rank',
                            accessor: 'rank',
                            Cell: ({ cell: { value } }) => {
                                return <>{value}</>
                            },
                        },
                        {


                            Header: () => {
                                const labelMap = {
                                    affiliate: 'Affiliate',
                                    campaign: 'Campaign',
                                    advertiser: 'Advertiser'
                                }
                                return <div style={{ textAlign: 'left' }}>{labelMap[type] || 'Item'}</div>
                            },



                            accessor: 'name',
                            Cell: (tableData) => {
                                const row = tableData.data[tableData.row.index]

                                if (type === 'affiliate') {
                                    return <Affiliate aid={row.affiliate_id} name={row.name} status={row.status} priority={row.priority} />
                                } else if (type === 'advertiser') {
                                    return <Advertiser id={row.merchant_id} name={row.name} />
                                } else {
                                    return (
                                        <div className="d-flex">
                                            <Campaign oid={row.offer_id} name={row.name} status={row.status} />
                                            {type === 'campaign' && (
                                                <span className="text-secondary ml-2">({row.full_name})</span>
                                            )}
                                        </div>
                                    )
                                }                                
                            },
                        },
                        {
                            Header: () => <div className='text-right'><Triangle size={10} /> Projected - (T-1)</div>,
                            accessor: 'projected_delta',
                            Cell: ({ cell: { value } }) => {
                                return (
                                    <div className="text-right">
                                        <Dollars value={value} />
                                    </div>
                                )
                            },
                        },
                        {
                            Header: () => <div style={{ textAlign: 'right' }}>Projected Margin</div>,
                            accessor: 'projected',
                            Cell: ({ cell: { value } }) => {
                                return (
                                    <div className="text-right">
                                        <Dollars value={value} />
                                    </div>
                                )
                            },
                        },
                        {
                            Header: () => <div className='text-right'>Current Margin</div>,
                            accessor: 'D0',
                            Cell: ({ cell: { value } }) => {
                                return (
                                    <div className="text-right">
                                        <Dollars value={value} />
                                    </div>
                                )
                            },
                        },
                        {
                            Header: () => <div style={{ textAlign: 'right' }}>T-1 Margin</div>,
                            accessor: 'D1',
                            Cell: ({ cell: { value } }) => {
                                return (
                                    <div style={{ textAlign: 'right' }}>
                                        <Dollars value={value} />
                                    </div>
                                )
                            },
                        },
                        {
                            Header: () => <div style={{ textAlign: 'right' }}>T-2 Margin</div>,
                            accessor: 'D2',
                            Cell: ({ cell: { value } }) => {
                                return (
                                    <div style={{ textAlign: 'right' }}>
                                        <Dollars value={value} />
                                    </div>
                                )
                            },
                        },
                        {
                            Header: () => (
                                <div style={{ textAlign: 'right' }}>
                                    <Triangle size={10} /> Margin
                                </div>
                            ),
                            accessor: 'delta',
                            Cell: ({ cell: { value } }) => {
                                let classes = `text-right `
                                classes += value < 0 ? `text-danger` : `text-success`
                                return (
                                    <div className={classes}>
                                        <Dollars value={value} />
                                    </div>
                                )
                            },
                        },
                        {
                            Header: () => (
                                <div style={{ textAlign: 'right' }}>
                                    <Triangle size={10} /> Margin %
                                </div>
                            ),
                            accessor: 'deltap',
                            Cell: (tableData) => {
                                let classes = `text-right `
                                let deltaValue = tableData.data[tableData.row.index].deltap
                                let delta = <span></span>

                                if (tableData.data[tableData.row.index].D1 > tableData.data[tableData.row.index].D2) {
                                    delta = <span style={{ color: 'green', fontSize: '10px' }}>&#9650;</span>
                                    classes += `text-success`
                                    if (deltaValue < 0) { deltaValue = deltaValue * -1 }
                                } else if (tableData.data[tableData.row.index].D1 < tableData.data[tableData.row.index].D2) {
                                    delta = <span style={{ color: 'red', fontSize: '10px' }}>&#9660;</span>
                                    classes += `text-danger`
                                    if (deltaValue > 0) { deltaValue = deltaValue * -1 }
                                } else {
                                    delta = ''
                                }

                                return (
                                    <div className={classes}>
                                        {Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(deltaValue)} % {delta}
                                    </div>
                                )
                            },
                        },
                        {
                            Header: () => <div style={{ textAlign: 'right' }}></div>,
                            accessor: type === 'affiliate' ? 'affiliate_id' : type === 'advertiser' ? 'merchant_id' : 'offer_id',
                            Cell: ({ cell: { value } }) => {
                                return (
                                    <div style={{ textAlign: 'right' }}>
                                        <Link
                                            to={
                                                type === 'affiliate'
                                                    ? `/reports/affiliateTrendDetail?aid=${value}`
                                                    : type === 'advertiser'
                                                        ? `/reports/advertiserTrendDetail?mid=${value}`
                                                        : `/reports/campaignTrendDetail?oid=${value}`
                                            }
                                            target="_blank"
                                        >

                                            Trends
                                        </Link>
                                    </div>
                                )
                            },
                        },
                    ]}
                />
            </React.Fragment>
        )
    }

    return (
        <React.Fragment>
            <div className='px-3 pb-3'>(Last Updated: {dataLoaded ? moment(lastUpdated).utc().format('YYYY-MM-DD HH:mm:ss') : spinner})</div>
            {dataLoaded ? report.length === 0 ? <div className="p-3">No stats for this parameters.</div> : getReportOutput() : <Loading></Loading>}
        </React.Fragment>
    )
}

export default ReportCampaignTrends
